import { useEffect, useMemo, useRef, useState } from 'react'
import { Box, Flex, Grid, Text, useT } from '@minetrace/ui'
import { AnimatePresence, motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { LuBookOpen, LuFiles, LuFolderOpen, LuFolderTree, LuSearch, LuX } from 'react-icons/lu'
import { expedienteService } from '../../api/services'
import type { ExpedienteSearchItemResponse } from '../../api/models/response/expediente'
import { EXPEDIENTE_TYPE_MINER } from '../../api/models/response/expediente'
import { accentFor, fonts, mineral, shadows } from '../../theme'
import { formatDate } from '../../lib/dates'

/** Expedientes vive en Identidad: rodocrosita. */
const acc = accentFor('identity')

const STATUS_CONFIG: Record<number, { label: string; plural: string; soft: string; color: string }> = {
  1: { label: 'Activo', plural: 'Activos', soft: mineral.sabanaSoft, color: mineral.sabanaDeep },
  0: { label: 'Borrador', plural: 'Borradores', soft: mineral.pizarraSoft, color: mineral.pizarra },
  2: { label: 'Revocado', plural: 'Revocados', soft: mineral.ajiSoft, color: mineral.aji },
}

/**
 * Directorio de expedientes de MINERO. Los de lote se consultan desde Auditoría de
 * Emergencia; aquí sólo vive el dosier del proveedor.
 *
 * Cada resultado es una carpeta: pestaña con el estado, titular, RUC, documentos y
 * fecha de aprobación, y dos entradas — la vista operativa (al hacer clic en la
 * carpeta) y el índice digital (botón propio).
 */
export default function ExpedientesPage() {
  const navigate = useNavigate()
  const { t } = useT()
  const inputRef = useRef<HTMLInputElement>(null)

  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [results, setResults] = useState<ExpedienteSearchItemResponse[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<number | null>(null)

  // Debounce
  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300)
    return () => clearTimeout(id)
  }, [query])

  // Fetch
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        // Provider directory only — batch (shipment) expedientes are accessed
        // through Auditoría de Emergencia, not this list.
        const hits = await expedienteService.search(debouncedQuery, { type: EXPEDIENTE_TYPE_MINER })
        if (!cancelled) setResults(hits)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Error al buscar expedientes')
          setResults([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => { cancelled = true }
  }, [debouncedQuery])

  const counts = useMemo(() => {
    const c: Record<number, number> = { 0: 0, 1: 0, 2: 0 }
    for (const r of results ?? []) c[r.status] = (c[r.status] ?? 0) + 1
    return c
  }, [results])
  const visible = useMemo(
    () => (results ?? []).filter(r => statusFilter === null || r.status === statusFilter),
    [results, statusFilter],
  )

  const go = (r: ExpedienteSearchItemResponse, archival = false) =>
    navigate(`/app/identidad/expedientes/${encodeURIComponent(r.code)}${archival ? '/electronico' : ''}`)

  return (
    <Flex direction="column" gap={6}>
      {/* Encabezado + buscador en una franja */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Flex align={{ base: 'stretch', lg: 'center' }} direction={{ base: 'column', lg: 'row' }} gap={{ base: 4, lg: 8 }}>
          <Flex align="center" gap={3} flexShrink={0}>
            <Flex w="36px" h="36px" borderRadius="10px" align="center" justify="center" bg={acc.color} color="#fff" flexShrink={0}>
              <LuFolderTree size={18} />
            </Flex>
            <Box>
              <Text fontFamily={fonts.display} fontWeight={800} fontSize="24px" letterSpacing="-0.03em" color={mineral.ink} lineHeight="1.1">
                {t('expedientes.title')}
              </Text>
              <Text fontSize="13px" color={mineral.ink3}>{t('expedientes.subtitle')}</Text>
            </Box>
          </Flex>

          <Flex flex={1} maxW={{ lg: '560px' }} ml={{ lg: 'auto' }} align="center" gap={2.5} h="46px" px={4} borderRadius="12px" bg="#fff"
            border={`1px solid ${query ? acc.color : mineral.lineStrong}`} boxShadow={query ? `0 0 0 3px ${acc.color}1a` : shadows.sm}
            transition="border-color .2s, box-shadow .2s" position="relative" overflow="hidden">
            <Box color={query ? acc.color : mineral.ink3} display="flex" transition="color .2s"><LuSearch size={18} /></Box>
            <input
              ref={inputRef}
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder={t('expedientes.searchPlaceholder')}
              aria-label={t('expedientes.searchPlaceholder')}
              style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: 15, color: mineral.ink }}
            />
            {query && (
              <Box as="button" {...{ type: 'button', 'aria-label': t('expedientes.clear'), title: t('expedientes.clear') }} display="flex" p={1} borderRadius="6px"
                color={mineral.ink3} cursor="pointer" _hover={{ bg: mineral.sunk, color: mineral.ink }}
                onClick={() => { setQuery(''); inputRef.current?.focus() }}>
                <LuX size={15} />
              </Box>
            )}
            {/* Barrido mientras busca */}
            <AnimatePresence>
              {loading && (
                <motion.span key="scan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, overflow: 'hidden' }}>
                  <motion.span animate={{ x: ['-40%', '140%'] }} transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
                    style={{ display: 'block', width: '40%', height: '100%', background: `linear-gradient(90deg, transparent, ${acc.color}, transparent)` }} />
                </motion.span>
              )}
            </AnimatePresence>
          </Flex>
        </Flex>
      </motion.div>

      {error && (
        <Box bg={mineral.ajiSoft} border={`1px solid ${mineral.aji}55`} borderRadius="14px" px={4} py={3}>
          <Text fontSize="sm" color={mineral.aji}>{error}</Text>
        </Box>
      )}

      {/* Filtros por estado */}
      <Flex align="center" gap={2} wrap="wrap">
        <Text fontFamily={fonts.display} fontWeight={700} fontSize="17px" color={mineral.ink} mr={2}>
          {debouncedQuery ? 'Coincidencias' : 'Expedientes de mineros'}
          <Text as="span" fontFamily={fonts.mono} fontSize="12px" fontWeight={500} color={mineral.ink3} ml={2}>{results?.length ?? 0}</Text>
        </Text>
        {[1, 0, 2].map(st => {
          const cfg = STATUS_CONFIG[st]!
          const on = statusFilter === st
          if (!counts[st] && !on) return null
          return (
            <Flex key={st} as="button" {...{ type: 'button' }} align="center" gap={1.5} h="28px" px={3} borderRadius="full" cursor="pointer"
              fontSize="12.5px" fontWeight={600} transition="all .15s" aria-pressed={on}
              bg={on ? cfg.color : cfg.soft} color={on ? '#fff' : cfg.color}
              onClick={() => setStatusFilter(on ? null : st)}>
              {cfg.plural}
              <Text as="span" fontFamily={fonts.mono} fontSize="11px" opacity={0.85}>{counts[st]}</Text>
            </Flex>
          )
        })}
      </Flex>

      {/* Carpetas */}
      {results === null ? (
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }} gap={5}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Box key={i} h="170px" borderRadius="16px" bg={mineral.sunk} opacity={1 - i * 0.12} mt="16px" />
          ))}
        </Grid>
      ) : visible.length === 0 ? (
        <Flex direction="column" align="center" py={16} gap={2} color={mineral.ink3}>
          <LuFolderOpen size={34} color={mineral.ink4} />
          <Text fontSize="14px">{debouncedQuery ? t('expedientes.emptyResults') : t('expedientes.emptyInitial')}</Text>
        </Flex>
      ) : (
        <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', xl: 'repeat(3, 1fr)' }} gap={5}>
          {visible.map((r, idx) => {
            const s = STATUS_CONFIG[r.status] ?? STATUS_CONFIG[0]!
            return (
              <motion.div key={r.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22, delay: Math.min(idx, 12) * 0.03 }} whileHover={{ y: -3 }}>
                <Box position="relative" pt="16px" role="group" cursor="pointer" onClick={() => go(r)}>
                  {/* Pestaña de la carpeta con el estado */}
                  <Flex position="absolute" top={0} left="18px" h="24px" px={2.5} align="center" gap={1.5} borderTopRadius="9px"
                    bg={s.soft} color={s.color} fontSize="10px" fontWeight={700} letterSpacing="0.06em" textTransform="uppercase" zIndex={1}>
                    <Box w="6px" h="6px" borderRadius="full" bg={s.color} />
                    {s.label}
                  </Flex>
                  <Box position="relative" borderRadius="16px" bg={mineral.surface} border={`1px solid ${mineral.line}`} boxShadow={shadows.sm}
                    overflow="hidden" transition="box-shadow .2s, border-color .2s"
                    _groupHover={{ boxShadow: shadows.md, borderColor: `${acc.color}66` }}>
                    <Box h="3px" bg={s.color} opacity={0.7} />
                    <Box px={5} pt={4} pb={4}>
                      <Text fontFamily={fonts.mono} fontSize="12px" fontWeight={600} color={mineral.ink3}>{r.code}</Text>
                      <Text fontFamily={fonts.display} fontWeight={700} fontSize="16px" color={mineral.ink} lineHeight="1.25" mt={1} lineClamp={2} minH="40px">
                        {r.companyLegalName ?? '—'}
                      </Text>
                      {r.companyRuc && (
                        <Text fontFamily={fonts.mono} fontSize="12px" color={mineral.ink2} mt={1}>RUC {r.companyRuc}</Text>
                      )}
                    </Box>
                    <Flex px={5} py={3} align="center" gap={3} borderTop={`1px dashed ${mineral.line}`} bg={mineral.surfaceAlt}>
                      <Flex align="center" gap={1.5} fontSize="12px" color={mineral.ink2}>
                        <LuFiles size={13} color={acc.color} />
                        <Text fontWeight={600}>{r.itemCount}</Text>
                        <Text color={mineral.ink3}>{t('expedientes.items')}</Text>
                      </Flex>
                      {r.approvedAt && (
                        <Text fontSize="12px" color={mineral.ink3} lineClamp={1}>· {formatDate(r.approvedAt)}</Text>
                      )}
                      <Flex as="button" {...{ type: 'button', title: 'Índice digital' }} ml="auto" align="center" gap={1.5} h="28px" px={2.5} borderRadius="8px"
                        border={`1px solid ${mineral.line}`} bg="#fff" color={mineral.ink2} fontSize="12px" fontWeight={600} cursor="pointer" flexShrink={0}
                        _hover={{ borderColor: acc.color, color: acc.deep }} transition="all .15s"
                        onClick={(e: React.MouseEvent) => { e.stopPropagation(); go(r, true) }}>
                        <LuBookOpen size={13} /> Índice
                      </Flex>
                    </Flex>
                  </Box>
                </Box>
              </motion.div>
            )
          })}
        </Grid>
      )}
    </Flex>
  )
}
